import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { siteSettingsService } from '../../services/siteSettingsService';
import { HowItWorksSkeleton } from '../../components/shared/ClientPageSkeleton';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  ArrowRight,
  Star,
  Search,
  Calendar,
  CreditCard,
  CheckCircle,
  Shield,
  MessageSquare,
  Users,
} from 'lucide-react';

const ICON_MAP = {
  Search,
  Calendar,
  CreditCard,
  CheckCircle,
  Star,
  Shield,
  MessageSquare,
  Users,
};

const STEP_IMAGES = [
  'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1200&q=80',
];

/** Defaults aligned with home MarketingStepsBand + enjoy step */
const DEFAULT_HOW_IT_WORKS = {
  hero: {
    badge: 'How it works',
    title: 'Book vendors in four calm steps',
    subtitle:
      'Browse verified vendors, compare packages, and lock the date — from first search to event day.',
    primaryButtonText: 'Browse vendors',
    primaryButtonLink: '/browse-vendors',
    secondaryButtonText: 'Become a vendor',
    secondaryButtonLink: '/vendor-signup',
  },
  steps: [
    {
      number: '01',
      icon: 'Search',
      title: 'Search',
      description:
        'What you need and where the party is — markets and categories, not guesswork.',
    },
    {
      number: '02',
      icon: 'Calendar',
      title: 'Compare',
      description:
        'Portfolios, packages, and reviews side by side so the shortlist feels obvious.',
    },
    {
      number: '03',
      icon: 'CreditCard',
      title: 'Book',
      description:
        'Lock the date with secure checkout, confirmation, and a clear path to event day.',
    },
    {
      number: '04',
      icon: 'CheckCircle',
      title: 'Enjoy',
      description:
        'Stay in touch with your vendor, get ready for the day, and enjoy the event.',
    },
  ],
  features: {
    title: 'Why book on VendaList',
    subtitle: 'Clarity, trust, and a calm path from shortlist to booked.',
    items: [
      {
        icon: 'Shield',
        title: 'Verified vendors',
        description: 'Providers are checked so you can book with more confidence.',
      },
      {
        icon: 'Star',
        title: 'Real reviews',
        description: 'Read feedback from past clients before you decide.',
      },
      {
        icon: 'CreditCard',
        title: 'Secure payments',
        description: 'Checkout is protected with industry-standard processing.',
      },
      {
        icon: 'MessageSquare',
        title: 'Direct messaging',
        description: 'Ask questions and customize details with the vendor.',
      },
      {
        icon: 'Calendar',
        title: 'Easy scheduling',
        description: 'See availability and book your preferred date.',
      },
      {
        icon: 'Users',
        title: 'Support when you need it',
        description: 'Our team helps if something goes sideways.',
      },
    ],
  },
  cta: {
    title: 'Ready when the date is',
    subtitle: 'Search once, shortlist fast, book with confidence.',
    primaryButtonText: 'Browse vendors',
    primaryButtonLink: '/browse-vendors',
    secondaryButtonText: 'Create account',
    secondaryButtonLink: '/register',
  },
};

const AUDIENCES = [
  {
    eyebrow: 'For guests',
    title: 'Find. Compare. Book.',
    description:
      'Photo-led listings, clear packages, and messaging — so planning feels lighter from the first search.',
    cta: 'Browse vendors',
    href: '/browse-vendors',
    image:
      'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80',
  },
  {
    eyebrow: 'For vendors',
    title: 'List. Get discovered. Get booked.',
    description:
      'Open your calendar, set packages, and show up in searches from planners already looking in your market.',
    cta: 'Become a vendor',
    href: '/vendor-signup',
    image:
      'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1200&q=80',
  },
];

const getIcon = (name) => ICON_MAP[name] || Star;

const normalizeBrowseLink = (link) => {
  if (!link) return '/browse-vendors';
  const path = link.startsWith('/') ? link : `/${link}`;
  if (path === '/browse' || path === '/browse/') return '/browse-vendors';
  return path;
};

const HowItWorks = () => {
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const hiw = settingsResponse?.data?.how_it_works;
  const hero = { ...DEFAULT_HOW_IT_WORKS.hero, ...hiw?.hero };
  const steps =
    Array.isArray(hiw?.steps) && hiw.steps.length > 0
      ? hiw.steps
      : DEFAULT_HOW_IT_WORKS.steps;
  const featuresSection = {
    ...DEFAULT_HOW_IT_WORKS.features,
    ...hiw?.features,
    items: hiw?.features?.items?.length
      ? hiw.features.items
      : DEFAULT_HOW_IT_WORKS.features.items,
  };
  const cta = { ...DEFAULT_HOW_IT_WORKS.cta, ...hiw?.cta };

  if (isLoading) {
    return <HowItWorksSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Compact intro under solid black header */}
      <section className="border-b border-m-border-light bg-white px-4 py-14 sm:px-6 sm:py-20">
        <MarketingReveal className="mx-auto max-w-3xl text-center">
          {hero.badge ? (
            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-m-ink-faint">
              {hero.badge}
            </p>
          ) : null}
          <h1
            className="marketing-hero-title mb-4"
            style={{ color: '#0a0a0a' }}
          >
            {hero.title}
          </h1>
          {hero.subtitle ? (
            <p
              className="mx-auto mb-8 max-w-xl text-[1.05rem] leading-relaxed"
              style={{ color: '#6b6b6b' }}
            >
              {hero.subtitle}
            </p>
          ) : null}
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            {hero.primaryButtonText ? (
              <Link
                to={normalizeBrowseLink(hero.primaryButtonLink)}
                className="marketing-btn marketing-btn-ink gap-2"
              >
                {hero.primaryButtonText}
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : null}
            {hero.secondaryButtonText ? (
              <Link
                to={normalizeBrowseLink(hero.secondaryButtonLink)}
                className="inline-flex h-[var(--m-control-h)] items-center justify-center gap-2 rounded-full border border-m-border-light px-6 text-sm font-medium text-m-ink transition-colors hover:border-m-ink"
              >
                {hero.secondaryButtonText}
              </Link>
            ) : null}
          </div>
        </MarketingReveal>
      </section>

      {/* Alternating photo + step story */}
      <section className="bg-m-canvas-light px-4 py-16 sm:px-6 sm:py-24">
        <MarketingReveal className="mx-auto mb-12 max-w-6xl sm:mb-16">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-m-ink-faint">
            The guest journey
          </p>
          <h2 className="marketing-section-title max-w-xl" style={{ color: '#0a0a0a' }}>
            From search to celebration
          </h2>
        </MarketingReveal>

        <div className="mx-auto flex max-w-6xl flex-col gap-14 sm:gap-20">
          {steps.map((step, index) => {
            const number = step.number || String(index + 1).padStart(2, '0');
            const image = STEP_IMAGES[index % STEP_IMAGES.length];
            const reverse = index % 2 === 1;

            return (
              <MarketingReveal key={`${number}-${step.title}`}>
                <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                  <div
                    className={`overflow-hidden rounded-2xl ${
                      reverse ? 'lg:order-2' : ''
                    }`}
                  >
                    <img
                      src={image}
                      alt=""
                      className="aspect-[5/4] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div
                    className={reverse ? 'lg:order-1' : ''}
                    style={{ color: '#0a0a0a' }}
                  >
                    <p
                      className="mb-4 text-[11px] font-medium uppercase tracking-[0.16em]"
                      style={{ color: '#8a8a8a' }}
                    >
                      Step {number}
                    </p>
                    <h3
                      className="mb-4 text-[1.75rem] font-bold tracking-tight sm:text-[2rem]"
                      style={{ color: '#0a0a0a' }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="max-w-md text-[1.05rem] leading-relaxed"
                      style={{ color: '#6b6b6b' }}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              </MarketingReveal>
            );
          })}
        </div>
      </section>

      {/* Guests / Vendors */}
      <section className="bg-white px-4 py-20 sm:px-6 sm:py-28">
        <MarketingReveal className="mx-auto mb-10 max-w-6xl sm:mb-12">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-m-ink-faint">
            Two sides of the marketplace
          </p>
          <h2 className="marketing-section-title max-w-xl" style={{ color: '#0a0a0a' }}>
            Built for planners and vendors
          </h2>
        </MarketingReveal>

        <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-2 lg:gap-5">
          {AUDIENCES.map((item) => (
            <MarketingReveal key={item.eyebrow}>
              <article className="overflow-hidden rounded-2xl bg-m-canvas-light">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.image}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="px-7 py-8 sm:px-8 sm:py-9" style={{ color: '#0a0a0a' }}>
                  <p
                    className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em]"
                    style={{ color: '#8a8a8a' }}
                  >
                    {item.eyebrow}
                  </p>
                  <h3
                    className="mb-3 text-xl font-bold tracking-tight sm:text-2xl"
                    style={{ color: '#0a0a0a' }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="mb-7 max-w-md text-[15px] leading-relaxed"
                    style={{ color: '#6b6b6b' }}
                  >
                    {item.description}
                  </p>
                  <Link
                    to={item.href}
                    className="marketing-btn marketing-btn-ink inline-flex w-fit gap-2"
                  >
                    {item.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            </MarketingReveal>
          ))}
        </div>
      </section>

      {/* Why book */}
      <section
        className="px-4 py-20 sm:px-6 sm:py-28"
        style={{ backgroundColor: '#3a3a3a' }}
      >
        <MarketingReveal className="mx-auto mb-12 max-w-6xl sm:mb-14">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-white/55">
            Why VendaList
          </p>
          <h2
            className="marketing-section-title mb-3"
            style={{ color: '#ffffff' }}
          >
            {featuresSection.title}
          </h2>
          {featuresSection.subtitle ? (
            <p className="max-w-xl text-[1.05rem] leading-relaxed text-white/70">
              {featuresSection.subtitle}
            </p>
          ) : null}
        </MarketingReveal>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
          {(featuresSection.items || []).map((feature, index) => {
            const Icon = getIcon(feature.icon);
            return (
              <div
                key={index}
                className="rounded-2xl bg-white px-6 py-7"
                style={{ color: '#0a0a0a' }}
              >
                <Icon className="mb-4 h-5 w-5" style={{ color: '#0a0a0a' }} />
                <h3
                  className="mb-2 text-base font-bold tracking-tight"
                  style={{ color: '#0a0a0a' }}
                >
                  {feature.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-white px-4 py-20 sm:px-6 sm:py-24">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#0a0a0a' }}>
            {cta.title}
          </h2>
          {cta.subtitle ? (
            <p
              className="mb-8 text-[1.05rem] leading-relaxed"
              style={{ color: '#6b6b6b' }}
            >
              {cta.subtitle}
            </p>
          ) : null}
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            {cta.primaryButtonText ? (
              <Link
                to={normalizeBrowseLink(cta.primaryButtonLink)}
                className="marketing-btn marketing-btn-ink gap-2"
              >
                {cta.primaryButtonText}
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : null}
            {cta.secondaryButtonText ? (
              <Link
                to={normalizeBrowseLink(cta.secondaryButtonLink)}
                className="inline-flex h-[var(--m-control-h)] items-center justify-center gap-2 rounded-full border border-m-border-light px-6 text-sm font-medium text-m-ink transition-colors hover:border-m-ink"
              >
                {cta.secondaryButtonText}
              </Link>
            ) : null}
          </div>
        </MarketingReveal>
      </section>
    </div>
  );
};

export default HowItWorks;
